/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";

import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { ContainerPosition, ContainerStatus, Event } from "../../models/marine.traffic";
import { MilestoneReference, MilestoneType } from "../../models/milestone";
import { Shipment } from "../../models/shipments";
import { calculateDelayedDays } from "../../util/calculateDelayedDays";
import { ShipmentStatus, ShipmentType } from "../../util/enums";
import { renderMilestoneDateByTypeAndReference } from "../Generic/MilestoneDates";
import { TrackingEmptySection } from "./EmptySection";
import { MultipleEvents } from "./Events/components/MultipleEvents";
import { TrackingHeader } from "./Header";
import LivePosition from "./LivePosition";
import { VoyageMapInformationProps } from "./Map";
import { EmptyState } from "./PortalEmptyState";
import StatusProgressBar from "./ProgressBar";
import ShipmentDetails from "./ShipmentDetails";
import useStyles, { DetailSection, HeaderSection, RouterSection, Wrapper } from "./styles";
import LiveTrackingUnAvailable from "./LiveTrackingUnAvailable";
import { useAppState } from "Context/StateContext";

// -------------------
// Helper Functions
// -------------------
function addDotAfterYear(dateString: string): string {
  const currentYear = new Date().getFullYear().toString();
  const [datePart] = dateString.split(" ");
  const year = datePart.split("/")[2];

  if (parseInt(year, 10) <= parseInt(currentYear, 10)) {
    return dateString.replace(year, `${year} •`);
  }

  return dateString;
}

export const formatDateUTC = (date?: string) => {
  if (!date) return undefined;

  return addDotAfterYear(`${formatInTimeZone(date, "UTC", "dd/MM/yyyy  hh:mm aa")} UTC`);
};

// -------------------
// Types
// -------------------
export interface ProgressBarContainerEvent {
  eventTypeCode?: string;
  eventTypeDescription: string;
  eventLocationName: string;
  nextEventDateTime: string;
  iconName?: string;
  status: string;
  portId?: number;
  vesselName?: string;
  events?: Event[];
}

interface VoyageDetailsProps {
  shipment?: Shipment;
  goBack?: () => void;
  viewLivePosition?: boolean;
  isOnViewComponent?: boolean;
  containerStatus?: ContainerStatus;
  containerPosition?: ContainerPosition;
  isCreate?: boolean;
  externalMapIsLoaded?: boolean;
  isCustomerPortal?: boolean;
  isFromMaintenance?: boolean;
}

// -------------------
// VoyageDetails Component
// -------------------
/**
 * Renders the live tracking map tab for shipment tracking details.
 * Displays shipment summary, live position on the map, events timeline, and progress bar.
 */

export const VoyageDetails = (props: VoyageDetailsProps) => {
  const {
    shipment,
    viewLivePosition = true,
    containerStatus,
    containerPosition,
    isCreate,
    externalMapIsLoaded,
    isCustomerPortal = false,
    isFromMaintenance = false,
  } = props;

  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const isShipmentTrackingEnabledFromUser = useMemo(() => {
    return (
      (currentUser?.company?.airTrackingEnabled && shipment?.shipmentType === ShipmentType.AIR) ||
      (currentUser?.company?.seaTrackingEnabled && shipment?.shipmentType === ShipmentType.SEA)
    );
  }, [currentUser]);

  const isShipmentTrackingEnabledFromShipmentCompany = useMemo(() => {
    return (
      (shipment?.company?.airTrackingEnabled && shipment?.shipmentType === ShipmentType.AIR) ||
      (shipment?.company?.seaTrackingEnabled && shipment?.shipmentType === ShipmentType.SEA)
    );
  }, [shipment]);
  // Extract progress bar data from containerStatus.
  const statusProgressBar = useMemo(() => containerStatus?.statusProgress, [containerStatus]);

  // Determine if the shipment is an air shipment.
  const isAirTracking = useMemo(() => shipment?.shipmentType === ShipmentType.AIR, [shipment]);

  // Determine if shipment type is either SEA or AIR.
  const isShipmentOfTypeSeaOrAir = useMemo(() => {
    return shipment?.shipmentType === ShipmentType.SEA || isAirTracking;
  }, [shipment, isAirTracking]);

  // Compute active progress step; default to length of events if activeStep is -1.
  const activeStep = useMemo(() => {
    if (statusProgressBar && statusProgressBar.activeStep !== -1) {
      return statusProgressBar.activeStep;
    }

    return statusProgressBar?.events.length || 0;
  }, [statusProgressBar]);

  // Calculate live container position from containerPosition.
  const currentContainerPosition = useMemo((): VoyageMapInformationProps | undefined => {
    if (containerPosition && Object.keys(containerPosition).length > 0) {
      const { LAT, LON, TIMESTAMP, SPEED } = containerPosition;
      const latLng = { lat: Number(LAT), lng: Number(LON) };

      return {
        vesselName:
          shipment?.shipmentTracking?.vesselPosition?.vesselId || containerStatus?.vesselName,
        position: latLng,
        lastUpdate: TIMESTAMP ? new Date(TIMESTAMP) : "",
        speed: SPEED ?? "",
      };
    }

    return undefined;
  }, [shipment, containerStatus, containerPosition]);

  // Fallback to last known position if current position is unavailable.
  const lastContainerPosition = useMemo((): VoyageMapInformationProps | undefined => {
    if (currentContainerPosition) {
      return shipment?.shipmentTracking?.nextPortName
        ? currentContainerPosition
        : currentContainerPosition;
    }

    return containerStatus?.lastPosition;
  }, [currentContainerPosition, shipment, containerStatus]);

  // Retrieve all positions and split them into concluded/in-progress.
  const allPositions = useMemo(() => containerStatus?.allPositions || [], [containerStatus]);
  const positions = useMemo(
    () => containerStatus?.position || { positionsConclude: [], positionsInProgress: [] },
    [containerStatus]
  );

  // Helper functions to obtain ETA values based on shipment type and configuration.
  const getPlannedETA = () => {
    if (shipment?.company?.configs?.isShipmentPlannedEtaAccessDisabled) return undefined;
    if (isAirTracking) {
      return renderMilestoneDateByTypeAndReference(
        shipment?.milestones || [],
        MilestoneType.ESTIMATED,
        MilestoneReference.SHIPMENT.ESTIMATED.ORIGIN
      );
    }

    return (
      formatDateUTC(containerStatus?.plannedEta) ||
      formatDateUTC(shipment?.shipmentTracking?.finalPortPredictiveArrival)
    );
  };

  const getPredictiveETA = () => {
    if (shipment?.company?.configs?.isShipmentPlannedEtaAccessDisabled) return undefined;
    if (isAirTracking) {
      return renderMilestoneDateByTypeAndReference(
        shipment?.milestones || [],
        MilestoneType.ESTIMATED,
        MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
      );
    }

    return (
      containerStatus?.finalPortPredictiveArrivalUtcLast ||
      shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
    );
  };

  const getATAByType = () => {
    if (isAirTracking) {
      return containerStatus?.finalPortPredictiveArrivalUtc || "";
    }
    if (shipment?.company?.configs?.isShipmentPlannedEtaAccessDisabled) {
      return formatDateUTC(containerStatus?.finalPortActualArrivalUtc) || "";
    }

    return undefined;
  };

  const delayedDays = shipment?.company?.configs?.isShipmentPlannedEtaAccessDisabled
    ? calculateDelayedDays(
        shipment?.shipmentTracking?.finalPortPredictiveArrival,
        shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
      )
    : undefined;

  const etaData =
    containerStatus?.finalPortPredictiveArrivalUtcLast ||
    shipment?.shipmentTracking?.finalPortPredictiveArrivalLast;
  const etdData =
    containerStatus?.originPortPredictiveDepartureUtc ||
    shipment?.shipmentTracking?.originPortPredictiveDeparture;

  // -------------------
  // Early Return Conditions

  // -------------------
  // 1. If shipment is being created.

  if (isCreate) {
    return (
      <TrackingEmptySection
        title={translate("theresNoTrackingInformationYet")}
        description={translate(
          "thisTabWillShowSomeTrackingInformationIfAvailableAfterYouCreateTheShipment"
        )}
        isCreate={isCreate}
      />
    );
  }

  // . If shipment tracking service is not enabled for the shipment type.
  if (
    !(isShipmentTrackingEnabledFromShipmentCompany || isShipmentTrackingEnabledFromUser) &&
    !isCustomerPortal
  ) {
    return <LiveTrackingUnAvailable />;
  }

  // 2. If shipment is neither SEA nor AIR OR tracking data is missing (and not in customer portal mode).
  if (
    shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.TRACKING_NOT_AVAILABLE ||
    (shipment?.shipmentType !== ShipmentType.SEA && shipment?.shipmentType !== ShipmentType.AIR) || // TO DO: create a function to this
    (!shipment?.shipmentTracking && !isCustomerPortal)
  ) {
    return (
      <TrackingEmptySection
        title={translate("trackingNotAvailable")}
        description={translate("thereIsNoTrackingAvailableForThisShipment")}
      />
    );
  }

  // 3. If shipment tracking status is waiting for update.
  if (shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.WAITING_FOR_UPDATE) {
    return (
      <TrackingEmptySection
        title={translate("waitingForUpdate")}
        description={translate(
          "weReStillValidatingInformationToSeeIfThisShipmentIsTrackableRefreshThisPageInAFewMinutes"
        )}
      />
    );
  }

  // 4. If tracking is not available (for customer portal or based on shipment status).
  if (!shipment?.shipmentTracking && isCustomerPortal) {
    return (
      <EmptyState.Root>
        <EmptyState.Item
          xs={12}
          md={isShipmentOfTypeSeaOrAir ? 8 : 12}
          lg={isShipmentOfTypeSeaOrAir ? 9 : 12}
          className={isShipmentOfTypeSeaOrAir ? classes.borderRight : ""}
        >
          <EmptyState.SubItem className={classes.pComponentLeft}>
            <ShipmentDetails
              shipment={shipment}
              containerStatus={containerStatus}
              isCustomerPortal={isCustomerPortal}
            />
          </EmptyState.SubItem>
          <EmptyState.SubItem className={classes.pComponentRight}>
            {isShipmentOfTypeSeaOrAir && (
              <MultipleEvents shipment={shipment} containerStatus={containerStatus} />
            )}
          </EmptyState.SubItem>
        </EmptyState.Item>
        <EmptyState.Item xs={12} md={4} lg={3}>
          <EmptyState.SubItem className={classes.paddingDefault}>
            {isShipmentOfTypeSeaOrAir && (
              <StatusProgressBar
                steps={(statusProgressBar?.events as any) || []}
                activeStep={activeStep}
                isAirTracking={isAirTracking}
              />
            )}
          </EmptyState.SubItem>
        </EmptyState.Item>
      </EmptyState.Root>
    );
  }

  // -------------------
  // Main Render
  // -------------------
  return (
    <Wrapper isCustomerPortal={isCustomerPortal}>
      <HeaderSection isFromMaintenance={isFromMaintenance}>
        <TrackingHeader
          ATA={getATAByType()}
          plannedETA={getPlannedETA()}
          delayedDays={delayedDays}
          predictedETA={getPredictiveETA()}
          shipmentStatus={shipment?.shipmentTracking?.shipmentStatus}
          atd={
            containerStatus?.originPortActualDepartureUtc ||
            shipment?.shipmentTracking?.originPortActualDeparture
          }
          steps={(statusProgressBar?.events as any) || []}
          activeStep={activeStep}
          locations={containerStatus?.transportationPlan?.locations}
          shipmentType={shipment?.shipmentType}
          originPort={containerStatus?.originPortName}
          finalPort={containerStatus?.finalPortName}
          etd={etdData}
          eta={etaData}
        />
      </HeaderSection>
      <DetailSection isFromMaintenance={isFromMaintenance}>
        <ShipmentDetails
          shipment={shipment}
          containerStatus={containerStatus}
          isCustomerPortal={isCustomerPortal}
          isAirtracking={isAirTracking}
        />
        <LivePosition
          lastContainerPosition={lastContainerPosition}
          allPositions={allPositions}
          positions={positions}
          view={!isAirTracking && viewLivePosition}
          statusProgressBar={statusProgressBar}
          externalMapIsLoaded={externalMapIsLoaded}
          isAirTracking={isAirTracking}
          isCustomerPortal={isCustomerPortal}
        />
        <MultipleEvents
          shipment={shipment}
          containerStatus={containerStatus}
          isAirTracking={isAirTracking}
          isCustomerPortal={isCustomerPortal}
        />
      </DetailSection>
      {!isCustomerPortal && (
        <RouterSection isFromMaintenance={isFromMaintenance}>
          <StatusProgressBar
            steps={(statusProgressBar?.events as any) || []}
            activeStep={
              statusProgressBar && statusProgressBar?.activeStep !== -1
                ? statusProgressBar?.activeStep
                : (statusProgressBar?.events.length as any)
            }
            isAirTracking={isAirTracking}
          />
        </RouterSection>
      )}
    </Wrapper>
  );
};
